import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { getEquipmentsWithMatSer, getEquipmentsWithOrder } from '../store/actions/protocols.action';
import { isEquipmentsLoading } from '../store/selectors/protocols.selector';
import { AppState } from '../store/state/state';
import { isDefined, UserMasterInstanceToken } from '@evorait/pace-core';
import { getUserRoleType, USER_ROLES_TYPE } from '../types/user-types';
import { updateServiceActivityType } from 'libs/pace-forms/src/lib/store/actions/form.actions';
import { FormsConstants } from '@evorait/pace-forms';

const minLengthOfOrderNumber = 7;

@Component({
    templateUrl: './protocol-provisioning.component.html',
    styleUrls: ['./protocol-provisioning.component.scss'],
    standalone: false,
})
export class ProtocolProvisioningComponent implements OnInit, OnDestroy {
    public protocolProvisionFormGroup: FormGroup;
    public materialNumberControl = new FormControl();
    public serialNumberControl = new FormControl();
    public serviceActivityControl = new FormControl();
    public orderNumberControl = new FormControl('', Validators.minLength(minLengthOfOrderNumber));
    private matSerialFormGroup: FormGroup;

    private submit = new Subject<void>();
    private destroy = new Subject<void>();
    public isEquipmentsLoading$: Observable<boolean>;
    private readonly userMasterService = inject(UserMasterInstanceToken);

    public readonly userRoleType = USER_ROLES_TYPE;
    public readonly serviceActivityOptions = FormsConstants.SERVICE_ACTIVITY_TYPES;
    public dropDownSelectedValueText: string;
    public disableSubmit = true;
    public userRole$: Observable<USER_ROLES_TYPE>;

    public constructor(private readonly store: Store<AppState>) {
        this.isEquipmentsLoading$ = store.select(isEquipmentsLoading);
        this.userRole$ = this.userMasterService.getUserRole().pipe(map((role) => getUserRoleType(role)));
    }

    public ngOnInit(): void {
        this.setServiceActivityRequiredValidator();
        this.matSerialFormGroup = new FormGroup({
            materialNumber: this.materialNumberControl,
            serialNumber: this.serialNumberControl,
        });
        this.protocolProvisionFormGroup = new FormGroup({
            matSerialNumber: this.matSerialFormGroup,
            orderNumber: this.orderNumberControl,
            serviceActivity: this.serviceActivityControl,
        });
        this.submit
            .asObservable()
            .pipe(
                switchMap(() => this.getServiceActivityType()),
                map((serviceActivity) => {
                    const orderNumber = this.orderNumberControl.value;
                    const materialNumber = this.materialNumberControl.value as string;
                    const serialNumber = this.serialNumberControl.value as string;
                    if (orderNumber) {
                        this.store.dispatch(getEquipmentsWithOrder({ orderNumber }));
                    } else {
                        this.store.dispatch(
                            getEquipmentsWithMatSer({
                                materialNumber,
                                serialNumber,
                            }),
                        );
                    }
                    this.store.dispatch(updateServiceActivityType({ serviceActivity }));
                }),
                takeUntil(this.destroy),
            )
            .subscribe();

        this.orderNumberControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.destroy))
            .subscribe((orderNumber) => {
                this.materialNumberControl[orderNumber ? 'disable' : 'enable']({ emitEvent: false });
                this.serialNumberControl[orderNumber ? 'disable' : 'enable']({ emitEvent: false });
                this.disableSubmit = !orderNumber;
            });

        this.matSerialFormGroup.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.destroy))
            .subscribe((value: { materialNumber: FormControl; serialNumber: FormControl }) => {
                this.orderNumberControl[!!value.materialNumber || !!value.serialNumber ? 'disable' : 'enable']({
                    emitEvent: false,
                });
                this.disableSubmit = !(!!value.materialNumber && !!value.serialNumber);
            });
    }

    public ngOnDestroy(): void {
        this.destroy.next();
    }

    public submitForm(): void {
        this.submit.next();
    }

    public submitFormOnEnter(): void {
        if (!this.disableSubmit && this.protocolProvisionFormGroup.valid) {
            this.submitForm();
        }
    }

    public onSelectionChange(selectedOption: string): void {
        this.serviceActivityControl.setValue(selectedOption);
        this.dropDownSelectedValueText = selectedOption;
    }

    private getServiceActivityType(): Observable<FormsConstants.SERVICE_ACTIVITY_TYPES> {
        return this.userRole$.pipe(
            take(1),
            map((userRole) => {
                const roleToActivity: Partial<Record<USER_ROLES_TYPE, FormsConstants.SERVICE_ACTIVITY_TYPES>> = {
                    [USER_ROLES_TYPE.INSTALLATION]: FormsConstants.SERVICE_ACTIVITY_TYPES.INSTALLATION,
                    [USER_ROLES_TYPE.BIOMEDICAL]: FormsConstants.SERVICE_ACTIVITY_TYPES.MAINTENANCE,
                };

                const serviceActivityType = roleToActivity[userRole];

                if (isDefined(serviceActivityType)) {
                    this.serviceActivityControl.patchValue(serviceActivityType);
                }
                return this.serviceActivityControl.value;
            }),
        );
    }

    private setServiceActivityRequiredValidator(): void {
        this.userRole$.pipe(take(1)).subscribe((userRole) => {
            const required = [USER_ROLES_TYPE.SERVICE_PARTNER, USER_ROLES_TYPE.SHS_EMPLOYEE].includes(userRole);
            // eslint-disable-next-line @typescript-eslint/unbound-method
            this.serviceActivityControl.setValidators(required ? Validators.required : null);
            this.serviceActivityControl.updateValueAndValidity();
        });
    }
}
